import {
	aiKnowledgeMinimumRelevanceScore,
	type AiGuardrailSettings,
	type MembershipSummary,
	type TenantAiSettings,
} from '@livechat/shared';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
	getTenantAiSettings,
	publishTenantAiConfiguration,
	restoreTenantAiConfiguration,
	updateTenantAiSettings,
} from '../api.js';
import { useDateFormat } from '../date-format.js';
import { aiSettingsPageTitle, type AiSettingsSection } from '../ai-settings-navigation.js';
import { TenantAiAssignments } from './TenantAiAssignments.js';
import { AiAgentActivity } from './AiAgentActivity.js';
import { TenantAiEvaluation } from './TenantAiEvaluation.js';
import { TenantAiSkills } from './TenantAiSkills.js';
import { TenantAiSimulator } from './TenantAiSimulator.js';
import { TenantKnowledge } from './TenantKnowledge.js';

function normalisePhrases(values: string[]): string[] {
	const seen = new Set<string>();
	return values
		.map((value) => value.trim())
		.filter((value) => {
			const key = value.toLocaleLowerCase();
			if (!key || seen.has(key)) return false;
			seen.add(key);
			return true;
		});
}

export function TenantAi({
	membership,
	section = 'ai',
	knowledgeCollectionSlug,
	onKnowledgeCollectionSelected,
}: {
	membership: MembershipSummary;
	section?: AiSettingsSection;
	knowledgeCollectionSlug?: string;
	onKnowledgeCollectionSelected?: (slug?: string, replace?: boolean) => void;
}) {
	const { formatDateTime } = useDateFormat();
	const [settings, setSettings] = useState<TenantAiSettings>();
	const [loading, setLoading] = useState(true);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string>();
	const [message, setMessage] = useState<string>();
	const [knowledgeRevision, setKnowledgeRevision] = useState(0);
	const [knowledgeCollectionName, setKnowledgeCollectionName] = useState<string>();
	const [knowledgeHeadingFocusRequest, setKnowledgeHeadingFocusRequest] = useState(0);
	const pageHeading = useRef<HTMLHeadingElement>(null);
	const [settingsDirty, setSettingsDirty] = useState(false);
	const handleKnowledgeCollectionNameChange = useCallback(
		(name?: string, focusHeading = false) => {
			setKnowledgeCollectionName(name);
			if (focusHeading) setKnowledgeHeadingFocusRequest((request) => request + 1);
		},
		[],
	);
	useEffect(() => {
		if (section !== 'ai-knowledge' || !knowledgeCollectionSlug) {
			setKnowledgeCollectionName(undefined);
		}
	}, [knowledgeCollectionSlug, membership.tenantId, section]);
	useEffect(() => {
		if (
			knowledgeHeadingFocusRequest > 0 &&
			section === 'ai-knowledge' &&
			knowledgeCollectionSlug
		) {
			pageHeading.current?.focus();
		}
	}, [knowledgeCollectionSlug, knowledgeHeadingFocusRequest, section]);

	useEffect(() => {
		let current = true;
		setLoading(true);
		setError(undefined);
		void getTenantAiSettings(membership.tenantId)
			.then((loaded) => {
				if (current) {
					setSettings(loaded);
					setSettingsDirty(false);
				}
			})
			.catch((caught: unknown) => {
				if (current) {
					setError(
						caught instanceof Error ? caught.message : 'Could not load AI services',
					);
				}
			})
			.finally(() => {
				if (current) setLoading(false);
			});
		return () => {
			current = false;
		};
	}, [membership.tenantId]);

	async function save(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!settings || busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const aiGuardrails = {
				...settings.aiGuardrails,
				blockedInputPhrases: normalisePhrases(settings.aiGuardrails.blockedInputPhrases),
				blockedOutputPhrases: normalisePhrases(settings.aiGuardrails.blockedOutputPhrases),
			};
			const updated = await updateTenantAiSettings(membership.tenantId, {
				aiAgentsEnabled: settings.aiAgentsEnabled,
				aiCopilotEnabled: settings.aiCopilotEnabled,
				aiRoutingEvaluationEnabled: settings.aiRoutingEvaluationEnabled,
				aiGuardrails,
				aiKnowledgeRetrievalStrictness: settings.aiKnowledgeRetrievalStrictness,
				messageEnhanceEnabled: settings.messageEnhanceEnabled,
			});
			setSettings(updated);
			setSettingsDirty(false);
			setMessage(
				updated.draftChanged
					? 'Draft saved. Publish it when it is ready for new conversations.'
					: 'AI settings saved.',
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save AI settings');
		} finally {
			setBusy(false);
		}
	}

	function updateGuardrails(input: Partial<AiGuardrailSettings>): void {
		setSettings((current) =>
			current
				? {
						...current,
						aiGuardrails: { ...current.aiGuardrails, ...input },
						draftChanged: true,
					}
				: current,
		);
		setSettingsDirty(true);
		setMessage(undefined);
	}

	async function publish(): Promise<void> {
		if (!settings?.draftChanged || settingsDirty || busy) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await publishTenantAiConfiguration(membership.tenantId);
			setSettings(updated);
			setSettingsDirty(false);
			setMessage(`Published version ${updated.publishedVersion?.version ?? ''}.`);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not publish the Draft');
		} finally {
			setBusy(false);
		}
	}

	async function restore(versionId: string, version: number): Promise<void> {
		if (
			busy ||
			!window.confirm(`Restore previous version ${version} as a new Published version?`)
		) {
			return;
		}
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			const updated = await restoreTenantAiConfiguration(membership.tenantId, versionId);
			setSettings(updated);
			setSettingsDirty(false);
			setKnowledgeRevision((current) => current + 1);
			setMessage(
				`Version ${version} restored as Published version ${updated.publishedVersion?.version}.`,
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not restore that version');
		} finally {
			setBusy(false);
		}
	}

	return (
		<section className="settings-page tenant-ai-page">
			<div className="settings-section-heading">
				<h4 ref={pageHeading} tabIndex={-1}>
					{aiSettingsPageTitle(section, knowledgeCollectionName)}
				</h4>
				{section !== 'ai-knowledge' && (
					<p>Manage AI configuration, safe testing and publication for this company.</p>
				)}
			</div>

			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}

			{loading ? (
				<div className="empty-configuration">Loading AI services…</div>
			) : settings?.messageEnhanceAvailable ||
			  settings?.aiAgentsAvailable ||
			  settings?.aiCopilotAvailable ? (
				<form className="tenant-ai-form" onSubmit={(event) => void save(event)}>
					{settings.aiAgentsAvailable && section === 'ai' && (
						<section className="settings-card integration-panel ai-version-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Version and publish</h4>
								<p>
									AI settings, personas and knowledge assignments are edited as a
									Draft. New chats use only the Published version; active AI chats
									remain pinned to the version they started with.
								</p>
							</div>
							<div className="ai-version-current">
								<div>
									<span>Draft</span>
									<strong>
										{settings.draftChanged
											? 'Changes to publish'
											: 'Matches Published'}
									</strong>
								</div>
								<div>
									<span>Published</span>
									<strong>
										{settings.publishedVersion
											? `Version ${settings.publishedVersion.version}`
											: 'Not published'}
									</strong>
									{settings.publishedVersion && (
										<small>
											{settings.publishedVersion.publishedBy} ·{' '}
											{formatDateTime(settings.publishedVersion.publishedAt)}
										</small>
									)}
								</div>
							</div>
							<button
								className="primary-button"
								disabled={busy || settingsDirty || !settings.draftChanged}
								onClick={() => void publish()}
								type="button"
							>
								Publish Draft
							</button>
							{!!settings.previousVersions.length && (
								<details className="ai-version-history">
									<summary>
										Previous versions ({settings.previousVersions.length})
									</summary>
									<ul>
										{settings.previousVersions.map((version) => (
											<li key={version.id}>
												<div>
													<strong>Version {version.version}</strong>
													<span>
														{version.publishedBy} ·{' '}
														{formatDateTime(version.publishedAt)}
													</span>
												</div>
												<button
													className="secondary-button"
													disabled={busy}
													onClick={() =>
														void restore(version.id, version.version)
													}
													type="button"
												>
													Restore
												</button>
											</li>
										))}
									</ul>
								</details>
							)}
						</section>
					)}
					{settings.messageEnhanceAvailable && section === 'ai' && (
						<section className="settings-card integration-panel tenant-ai-service-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Message Enhance</h4>
								<p>
									Allow staff to improve drafted replies with the configured AI
									service.
								</p>
							</div>

							<label className="switch-row">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enabled</span>
									<span className="settings-option-description">
										Make Message Enhance available to staff in this company.
									</span>
								</div>
								<input
									checked={settings.messageEnhanceEnabled}
									onChange={(event) => {
										const checked = event.currentTarget.checked;
										setSettings((current) =>
											current
												? {
														...current,
														messageEnhanceEnabled: checked,
													}
												: current,
										);
										setSettingsDirty(true);
										setMessage(undefined);
									}}
									type="checkbox"
								/>
							</label>
						</section>
					)}
					{settings.aiCopilotAvailable && section === 'ai' && (
						<section className="settings-card integration-panel tenant-ai-service-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Copilot</h4>
								<p>
									Allow human staff to opt in to draft-only assistance while they
									work in conversations.
								</p>
							</div>
							<label className="switch-row">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enabled</span>
									<span className="settings-option-description">
										Make AI Copilot available for staff to enable in their own
										user settings.
									</span>
								</div>
								<input
									checked={settings.aiCopilotEnabled}
									onChange={(event) => {
										const checked = event.currentTarget.checked;
										setSettings((current) =>
											current
												? {
														...current,
														aiCopilotEnabled: checked,
														...(!checked
															? { aiRoutingEvaluationEnabled: false }
															: {}),
													}
												: current,
										);
										setSettingsDirty(true);
										setMessage(undefined);
									}}
									type="checkbox"
								/>
							</label>
							<label className="switch-row">
								<div className="settings-option-copy">
									<span className="settings-option-title">
										Automatic routing evaluation
									</span>
									<span className="settings-option-description">
										Analyse new visitor turns in the background so staff can
										review routing quality. Recommendations remain private and
										never transfer chats or change priority.
									</span>
								</div>
								<input
									checked={settings.aiRoutingEvaluationEnabled}
									disabled={!settings.aiCopilotEnabled}
									onChange={(event) => {
										const checked = event.currentTarget.checked;
										setSettings((current) =>
											current
												? {
														...current,
														aiRoutingEvaluationEnabled: checked,
													}
												: current,
										);
										setSettingsDirty(true);
										setMessage(undefined);
									}}
									type="checkbox"
								/>
							</label>
						</section>
					)}
					{settings.aiAgentsAvailable && section === 'ai' && (
						<section className="settings-card integration-panel tenant-ai-service-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">AI Agents</h4>
								<p>
									Allow trained AI team members to receive and answer customer
									chats.
								</p>
							</div>
							<label className="switch-row">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enabled</span>
									<span className="settings-option-description">
										Allow active AI agents to receive customer chats.
									</span>
								</div>
								<input
									checked={settings.aiAgentsEnabled}
									onChange={(event) => {
										const checked = event.currentTarget.checked;
										setSettings((current) =>
											current
												? {
														...current,
														aiAgentsEnabled: checked,
														draftChanged: true,
													}
												: current,
										);
										setSettingsDirty(true);
										setMessage(undefined);
									}}
									type="checkbox"
								/>
							</label>
							<label className="tenant-ai-strictness-setting">
								<span>Knowledge matching preset</span>
								<select
									onChange={(event) => {
										const strictness = event.currentTarget
											.value as TenantAiSettings['aiKnowledgeRetrievalStrictness'];
										setSettings((current) =>
											current
												? {
														...current,
														aiGuardrails: {
															...current.aiGuardrails,
															minimumRelevanceScore:
																aiKnowledgeMinimumRelevanceScore(
																	strictness,
																),
														},
														aiKnowledgeRetrievalStrictness: strictness,
														draftChanged: true,
													}
												: current,
										);
										setSettingsDirty(true);
										setMessage(undefined);
									}}
									value={settings.aiKnowledgeRetrievalStrictness}
								>
									<option value="relaxed">Relaxed</option>
									<option value="standard">Standard</option>
									<option value="strict">Strict</option>
								</select>
								<small>
									Sets the starting evidence score below. You can then fine-tune
									that score in Guardrails.
								</small>
							</label>
						</section>
					)}
					{settings.aiAgentsAvailable && section === 'ai-guardrails' && (
						<section className="settings-card integration-panel ai-guardrails-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Guardrails</h4>
								<p>
									Control when AI agents answer, ask for more detail or hand the
									chat to a person. Prompt-injection and source-attribution checks
									are always applied.
								</p>
							</div>
							<div className="ai-guardrail-grid">
								<label>
									<span>Minimum evidence score</span>
									<div className="ai-guardrail-range">
										<input
											max="0.95"
											min="0.05"
											onChange={(event) =>
												updateGuardrails({
													minimumRelevanceScore: Number(
														event.currentTarget.value,
													),
												})
											}
											step="0.05"
											type="range"
											value={settings.aiGuardrails.minimumRelevanceScore}
										/>
										<strong>
											{settings.aiGuardrails.minimumRelevanceScore.toFixed(2)}
										</strong>
									</div>
									<small>
										The strongest retrieved knowledge must meet this evidence
										score before the model can answer.
									</small>
								</label>
								<label>
									<span>When evidence is too weak</span>
									<select
										onChange={(event) =>
											updateGuardrails({
												lowConfidenceAction: event.currentTarget
													.value as AiGuardrailSettings['lowConfidenceAction'],
											})
										}
										value={settings.aiGuardrails.lowConfidenceAction}
									>
										<option value="clarify">Ask for more detail</option>
										<option value="handoff">Hand off to a person</option>
									</select>
								</label>
								<label>
									<span>Maximum clarification attempts</span>
									<select
										onChange={(event) =>
											updateGuardrails({
												maximumClarificationAttempts: Number(
													event.currentTarget.value,
												),
											})
										}
										value={settings.aiGuardrails.maximumClarificationAttempts}
									>
										<option value="0">Never clarify</option>
										<option value="1">Once per chat</option>
										<option value="2">Twice per chat</option>
										<option value="3">Three times per chat</option>
									</select>
								</label>
								<label>
									<span>Maximum response length</span>
									<input
										max="10000"
										min="250"
										onChange={(event) =>
											updateGuardrails({
												maximumResponseCharacters: Number(
													event.currentTarget.value,
												),
											})
										}
										step="50"
										type="number"
										value={settings.aiGuardrails.maximumResponseCharacters}
									/>
									<small>Between 250 and 10,000 characters.</small>
								</label>
							</div>
							<div className="ai-guardrail-phrases">
								<label>
									<span>Restricted visitor phrases</span>
									<textarea
										maxLength={2_525}
										onChange={(event) =>
											updateGuardrails({
												blockedInputPhrases:
													event.currentTarget.value.split(/\r?\n/),
											})
										}
										placeholder="One literal phrase per line"
										rows={4}
										value={settings.aiGuardrails.blockedInputPhrases.join('\n')}
									/>
									<small>Case-insensitive; up to 25 phrases.</small>
								</label>
								<label>
									<span>Restricted-input action</span>
									<select
										onChange={(event) =>
											updateGuardrails({
												restrictedInputAction: event.currentTarget
													.value as AiGuardrailSettings['restrictedInputAction'],
											})
										}
										value={settings.aiGuardrails.restrictedInputAction}
									>
										<option value="clarify">Redirect the visitor</option>
										<option value="handoff">Hand off to a person</option>
									</select>
								</label>
								<label>
									<span>Restricted reply phrases</span>
									<textarea
										maxLength={2_525}
										onChange={(event) =>
											updateGuardrails({
												blockedOutputPhrases:
													event.currentTarget.value.split(/\r?\n/),
											})
										}
										placeholder="One literal phrase per line"
										rows={4}
										value={settings.aiGuardrails.blockedOutputPhrases.join(
											'\n',
										)}
									/>
									<small>
										Generated replies containing these phrases are blocked.
									</small>
								</label>
								<label>
									<span>Output-validation action</span>
									<select
										onChange={(event) =>
											updateGuardrails({
												outputFailureAction: event.currentTarget
													.value as AiGuardrailSettings['outputFailureAction'],
											})
										}
										value={settings.aiGuardrails.outputFailureAction}
									>
										<option value="clarify">Ask for a safer question</option>
										<option value="handoff">Hand off to a person</option>
									</select>
								</label>
							</div>
						</section>
					)}
					{(section === 'ai' || section === 'ai-guardrails') && (
						<button className="primary-button tenant-ai-save" disabled={busy}>
							{busy ? 'Saving…' : 'Save AI settings'}
						</button>
					)}
				</form>
			) : (
				<div className="empty-configuration">
					No AI services are currently available for this company.
				</div>
			)}

			{settings?.aiAgentsAvailable && section === 'ai-knowledge' && (
				<TenantKnowledge
					collectionSlug={knowledgeCollectionSlug}
					onCollectionSelected={onKnowledgeCollectionSelected}
					onCollectionNameChange={handleKnowledgeCollectionNameChange}
					key={`${membership.tenantId}-${knowledgeRevision}`}
					onDraftChange={() =>
						setSettings((current) =>
							current ? { ...current, draftChanged: true } : current,
						)
					}
					tenantId={membership.tenantId}
				/>
			)}
			{settings?.aiAgentsAvailable && section === 'ai-simulator' && (
				<TenantAiSimulator disabled={settingsDirty} tenantId={membership.tenantId} />
			)}
			{settings?.aiAgentsAvailable && section === 'ai-evaluations' && (
				<TenantAiEvaluation
					disabled={settingsDirty}
					onDraftReset={(updated) => {
						setSettings(updated);
						setSettingsDirty(false);
						setKnowledgeRevision((current) => current + 1);
					}}
					tenantId={membership.tenantId}
				/>
			)}
			{section === 'ai-skills' && <TenantAiSkills tenantId={membership.tenantId} />}
			{section === 'ai-assignments' && (
				<TenantAiAssignments
					tenantId={membership.tenantId}
					onDraftChange={() =>
						setSettings((current) =>
							current ? { ...current, draftChanged: true } : current,
						)
					}
				/>
			)}
			{section === 'ai-activity' && <AiAgentActivity tenantId={membership.tenantId} />}
		</section>
	);
}
